/**
 * Vertices closer than this are the same, as in EdgesGeometry of three.js, since the triangles of
 * a glb file often have vertices of their own along their edges.
 */
const weldPrecision = 1e-4;

/**
 * Gets the edges of the shape of a mesh: where its surface bends by at least the angle, and where
 * it ends, but not the edges between the triangles of a flat or smoothly curved surface. As
 * EdgesGeometry of three.js, in typed arrays and without string keys, which is many times faster
 * for large models.
 * @param positions The x, y and z of each vertex.
 * @param index The vertices of each triangle, three by three, or the vertices in order without it.
 * @param thresholdAngle In degrees.
 * @returns The two ends of each edge, x, y and z, as for LineSegments.
 */
export function computeShapeEdges(
  positions: Float32Array,
  index: ArrayLike<number> | undefined,
  thresholdAngle = 30,
): Float32Array {
  const vertexCount = positions.length / 3;
  const triangleCount = Math.floor((index ? index.length : vertexCount) / 3);
  const welded = weldVertices(positions);
  const cosThreshold = Math.cos((thresholdAngle * Math.PI) / 180);
  const normals = new Float32Array(triangleCount * 3);

  // The edges with one triangle so far, by the welded vertices at their ends: the triangle, and the
  // vertices at the ends, in the order of the triangle.
  const open = new Map<number, number>();
  const openTriangle: number[] = [];
  const openEnds: number[] = [];
  // The vertices at the ends of the edges of the shape.
  const ends: number[] = [];

  const corners = [0, 0, 0];
  for (let triangle = 0; triangle < triangleCount; triangle++) {
    for (let i = 0; i < 3; i++) {
      corners[i] = index ? index[triangle * 3 + i] : triangle * 3 + i;
    }

    const [a, b, c] = corners;
    if (
      welded[a] === welded[b] ||
      welded[b] === welded[c] ||
      welded[c] === welded[a] ||
      !setNormal(positions, a, b, c, normals, triangle)
    ) {
      continue;
    }

    for (let i = 0; i < 3; i++) {
      const v0 = corners[i];
      const v1 = corners[(i + 1) % 3];
      const w0 = welded[v0];
      const w1 = welded[v1];
      const key = w0 < w1 ? w0 * vertexCount + w1 : w1 * vertexCount + w0;
      const slot = open.get(key);

      if (slot === undefined) {
        open.set(key, openTriangle.length);
        openTriangle.push(triangle);
        openEnds.push(v0, v1);
        continue;
      }

      // The second triangle of the edge, which is an edge of the shape if they bend enough. A third
      // one, of a non-manifold edge, opens it again.
      open.delete(key);
      const other = openTriangle[slot];
      const dot =
        normals[other * 3] * normals[triangle * 3] +
        normals[other * 3 + 1] * normals[triangle * 3 + 1] +
        normals[other * 3 + 2] * normals[triangle * 3 + 2];

      if (dot <= cosThreshold) {
        ends.push(openEnds[slot * 2], openEnds[slot * 2 + 1]);
      }
    }
  }

  // Where the surface ends.
  for (const slot of open.values()) {
    ends.push(openEnds[slot * 2], openEnds[slot * 2 + 1]);
  }

  const segments = new Float32Array(ends.length * 3);
  for (let i = 0; i < ends.length; i++) {
    segments[i * 3] = positions[ends[i] * 3];
    segments[i * 3 + 1] = positions[ends[i] * 3 + 1];
    segments[i * 3 + 2] = positions[ends[i] * 3 + 2];
  }

  return segments;
}

/**
 * Gets the same number for the vertices at the same position, see weldPrecision.
 */
function weldVertices(positions: Float32Array): Uint32Array {
  const count = positions.length / 3;
  const quantized = new Int32Array(positions.length);
  for (let i = 0; i < positions.length; i++) {
    quantized[i] = Math.round(positions[i] / weldPrecision);
  }

  const order = new Uint32Array(count);
  for (let i = 0; i < count; i++) {
    order[i] = i;
  }
  order.sort(
    (i, j) =>
      quantized[i * 3] - quantized[j * 3] ||
      quantized[i * 3 + 1] - quantized[j * 3 + 1] ||
      quantized[i * 3 + 2] - quantized[j * 3 + 2],
  );

  const welded = new Uint32Array(count);
  for (let k = 0; k < count; k++) {
    const i = order[k];
    const j = order[k - 1];
    const same =
      k > 0 &&
      quantized[i * 3] === quantized[j * 3] &&
      quantized[i * 3 + 1] === quantized[j * 3 + 1] &&
      quantized[i * 3 + 2] === quantized[j * 3 + 2];
    welded[i] = same ? welded[j] : i;
  }

  return welded;
}

/**
 * Sets the unit normal of the triangle. Returns false for a triangle without an area.
 */
function setNormal(
  p: Float32Array,
  a: number,
  b: number,
  c: number,
  normals: Float32Array,
  triangle: number,
) {
  const ux = p[b * 3] - p[a * 3];
  const uy = p[b * 3 + 1] - p[a * 3 + 1];
  const uz = p[b * 3 + 2] - p[a * 3 + 2];
  const vx = p[c * 3] - p[a * 3];
  const vy = p[c * 3 + 1] - p[a * 3 + 1];
  const vz = p[c * 3 + 2] - p[a * 3 + 2];
  const nx = uy * vz - uz * vy;
  const ny = uz * vx - ux * vz;
  const nz = ux * vy - uy * vx;
  const length = Math.hypot(nx, ny, nz);

  if (length === 0) {
    return false;
  }

  normals[triangle * 3] = nx / length;
  normals[triangle * 3 + 1] = ny / length;
  normals[triangle * 3 + 2] = nz / length;
  return true;
}
